<script setup lang="ts">
import { Phone, RefreshCw, Calendar, HelpCircle } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface Props {
  isLoading?: boolean;
  hasFilters?: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  refresh: [];
  clearFilters: [];
}>();
</script>

<template>
  <!-- Main container with responsive padding -->
  <Card class="mx-auto my-8 max-w-2xl border-none shadow-none bg-transparent">
    <div class="flex flex-col items-center justify-center p-6 md:p-12 text-center space-y-6">
      <!-- Icon with subtle animation -->
      <div
        class="relative flex h-24 w-24 md:h-32 md:w-32 items-center justify-center rounded-full bg-muted/50 transition-all duration-300">
        <Phone class="h-12 w-12 md:h-16 md:w-16 text-muted-foreground/60" />
      </div>

      <!-- Heading -->
      <div class="space-y-2">
        <h3 class="text-xl md:text-2xl font-semibold tracking-tight">
          {{ hasFilters ? 'No calls match your filters' : 'No calls yet' }}
        </h3>
        <p class="text-sm md:text-base text-muted-foreground max-w-md mx-auto leading-relaxed whitespace-break-spaces">
          {{
            hasFilters
              ? "Try adjusting your search criteria or date range to find what you're looking for."
              : 'Calls will appear here once they are recorded and processed. This usually takes a few minutes after a call ends.'
          }}
        </p>
      </div>

      <!-- Action buttons -->
      <div class="flex flex-col sm:flex-row items-center gap-3 pt-2">
        <Button v-if="hasFilters" variant="outline" size="lg" class="w-full sm:w-auto min-w-[160px]"
          @click="emit('clearFilters')">
          Clear filters
        </Button>

        <Button variant="outline" size="lg" class="w-full sm:w-auto min-w-[160px]" :disabled="isLoading"
          @click="emit('refresh')">
          <RefreshCw class="h-4 w-4 mr-2" :class="{ 'animate-spin': isLoading }" />
          Refresh
        </Button>
      </div>

      <!-- Helpful tips section -->
      <div class="mt-8 pt-6 border-t border-border/40 w-full max-w-md space-y-4">
        <div class="flex items-start gap-3 text-left">
          <Calendar class="h-5 w-5 text-muted-foreground/60 mt-0.5 flex-shrink-0" />
          <div class="space-y-1">
            <p class="text-sm font-medium">Check your date range</p>
            <p class="text-xs text-muted-foreground">
              Make sure you've selected a date range that includes call activity
            </p>
          </div>
        </div>

        <div class="flex items-start gap-3 text-left">
          <HelpCircle class="h-5 w-5 text-muted-foreground/60 mt-0.5 flex-shrink-0" />
          <div class="space-y-1">
            <p class="text-sm font-medium">Processing delay</p>
            <p class="text-xs text-muted-foreground">
              New calls may take 2-5 minutes to appear after they end
            </p>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>